import { useCallback } from 'react';
import { batch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { useAppDispatch } from '../../redux/hooks';
import { setExecutingAnalysis } from '../../redux/slices/executingAnalyses';
import { IAnalysisResponse } from '../apollo/responseTypes';
import { IPopulateBuilderAnalysisFieldsFragment } from '../apollo/graphqlSchema';
import { getAnalysisInfoFetchByType } from '../hooks/useAnalysisInfoFetch/utils';
import { getQueryTypePagePath } from '../routes';
import { useBuilderContext } from '../../components/Builder/BuilderContext';

/**
 * A special hook that will populate Builder with a new analysis after execution
 * Also, it will set justExecuted value to BuilderContext in order to
 * avoid Builder re-rendering
 */
const useExecutedReport = () => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { setJustExecuted } = useBuilderContext();

  return useCallback(
    (analysis: IAnalysisResponse | IPopulateBuilderAnalysisFieldsFragment) => {
      const { id: analysisId, queryType } = analysis;
      const analysisInfoFetchHandler = getAnalysisInfoFetchByType[queryType];

      batch(() => {
        analysisInfoFetchHandler(dispatch, analysis);
        dispatch(
          setExecutingAnalysis({
            analysisId,
            executedAt: analysis.executedAt,
            estimatedDuration: analysis.estimatedDuration ?? 0,
          }),
        );
      });

      setJustExecuted();

      navigate(getQueryTypePagePath(queryType, analysisId));
    },
    [dispatch, navigate, setJustExecuted],
  );
};

export default useExecutedReport;
